// Admin Dashboard Component
// Allows admins to view all users, manage roles, and delete users

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/AdminDashboard.css';

interface User {
  id: string;
  username: string;
  avatar: string;
  provider: 'github' | 'discord';
  is_moderator: boolean;
  is_admin: boolean;
  contributions: {
    notes: number;
    issues: number;
  };
  created_at: string;
  last_login: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const AdminDashboard: React.FC = () => {
  const { user, isAuthenticated, getAuthToken } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Check admin access
  if (!isAuthenticated || !user?.is_admin) {
    return (
      <div className="admin-dashboard">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You must be an administrator to access this page.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, search]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const url = new URL('/api/admin/users/list', window.location.origin);
      url.searchParams.set('page', pagination.page.toString());
      url.searchParams.set('limit', pagination.limit.toString());
      if (search) {
        url.searchParams.set('search', search);
      }

      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModerator = async (userId: string, currentStatus: boolean) => {
    if (actionLoading) return;

    setActionLoading(userId);
    try {
      const token = getAuthToken();
      const response = await fetch('/api/admin/users/update-role', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          is_moderator: !currentStatus,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update role');
      }

      // Refresh user list
      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId: string, username: string) => {
    if (actionLoading) return;

    const confirmed = confirm(
      `Are you sure you want to delete user "${username}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    setActionLoading(userId);
    try {
      const token = getAuthToken();
      const response = await fetch('/api/admin/users/delete', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }

      // Refresh user list
      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 on search
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>User Management</h1>
        <p className="dashboard-subtitle">
          Manage user roles and permissions
        </p>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search users by username..."
          value={search}
          onChange={handleSearchChange}
          className="search-input"
        />
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <>
          <div className="users-stats">
            <span>Total Users: {pagination.total}</span>
            <span>Page {pagination.page} of {pagination.totalPages}</span>
          </div>

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Provider</th>
                  <th>Role</th>
                  <th>Contributions</th>
                  <th>Joined</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr
                    key={u.id}
                    onClick={(e) => {
                      // Toggle expanded class on mobile
                      if (window.innerWidth <= 768) {
                        const row = e.currentTarget;
                        row.classList.toggle('expanded');
                      }
                    }}
                  >
                    <td className="user-cell" data-label="User">
                      <img
                        src={u.avatar}
                        alt={u.username}
                        className="user-avatar"
                      />
                      <span className="username">{u.username}</span>
                    </td>
                    <td data-label="Provider">
                      <span className={`provider-badge ${u.provider}`}>
                        {u.provider}
                      </span>
                    </td>
                    <td data-label="Role">
                      {u.is_admin ? (
                        <span className="role-badge admin">Admin</span>
                      ) : u.is_moderator ? (
                        <span className="role-badge moderator">Moderator</span>
                      ) : (
                        <span className="role-badge user">User</span>
                      )}
                    </td>
                    <td className="contributions-cell" data-label="Contributions">
                      <div className="contributions">
                        <span title="Notes">📄 {u.contributions.notes}</span>
                        <span title="Issues">🐛 {u.contributions.issues}</span>
                      </div>
                    </td>
                    <td data-label="Joined">{formatDate(u.created_at)}</td>
                    <td data-label="Last Login">{formatDate(u.last_login)}</td>
                    <td className="actions-cell" data-label="Actions" onClick={(e) => e.stopPropagation()}>
                      {!u.is_admin && (
                        <>
                          <button
                            onClick={() => handleToggleModerator(u.id, u.is_moderator)}
                            disabled={actionLoading === u.id}
                            className={`btn-action ${u.is_moderator ? 'btn-demote' : 'btn-promote'}`}
                            title={u.is_moderator ? 'Remove moderator' : 'Make moderator'}
                          >
                            {actionLoading === u.id ? '...' : u.is_moderator ? '👤' : '⭐'}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(u.id, u.username)}
                            disabled={actionLoading === u.id}
                            className="btn-action btn-delete"
                            title="Delete user"
                          >
                            {actionLoading === u.id ? '...' : '🗑️'}
                          </button>
                        </>
                      )}
                      {u.is_admin && (
                        <span className="admin-protected">Protected</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-pagination"
              >
                Previous
              </button>
              <span className="page-info">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="btn-pagination"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
